import { useState } from 'react'
import { toast } from 'sonner'
import { CheckCircle2, Circle, Copy, Link2, Loader2, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { FinanceDetailSectionCard } from '@/components/Financeiro/FinanceDetailSectionCard'
import {
  G5_LINK_STATUS_DESCRIPTIONS,
  G5_LINK_STATUS_LABELS,
  g5LinkStatusTone,
  type G5LinkStatus,
} from '@/components/Financeiro/g5LinkStatus'
import { PJ_AGRADA_STATUS_LABELS } from '@/components/PjAgrada/pjAgradaStatus'
import { formatInviteTimeRemaining } from '@/components/PjAgrada/pjAgradaFormat'
import type { FinanceRequestDetail } from '@/services/financeService'
import { financeService } from '@/services/financeService'
import { formatSecurityDate } from '@/components/Configuracao/behavioralSecurityHelpers'
import { cn } from '@/utils/cn'

type Props = {
  detail: FinanceRequestDetail
  canGenerateG5Invite: boolean
  onReload: () => void
}

function statusBadgeClass(tone: ReturnType<typeof g5LinkStatusTone>) {
  if (tone === 'emerald') return 'bg-emerald-500/10 text-emerald-600'
  if (tone === 'amber') return 'bg-amber-500/10 text-amber-700'
  if (tone === 'danger') return 'bg-danger/10 text-danger'
  return 'bg-white/[0.06] text-text-muted'
}

function G5LinkStatusCard({
  linkStatus,
  inviteUrl,
  expiresAt,
  usedAt,
  canCopyLink,
  onCopy,
}: {
  linkStatus: G5LinkStatus
  inviteUrl: string | null
  expiresAt: string | null
  usedAt: string | null
  canCopyLink: boolean
  onCopy: (url: string) => void
}) {
  const tone = g5LinkStatusTone(linkStatus)
  const isExpired = linkStatus === 'EXPIRADO'
  const timeLabel =
    expiresAt && linkStatus === 'AGUARDANDO_PREENCHIMENTO'
      ? formatInviteTimeRemaining(expiresAt, isExpired)
      : null

  return (
    <div className='rounded-xl border border-white/[0.06] bg-white/[0.03] p-4'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <p className='text-[10px] font-bold uppercase tracking-wider text-text-muted'>
            Status do link de cadastro
          </p>
          <span
            className={cn(
              'mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold',
              statusBadgeClass(tone),
            )}
          >
            {G5_LINK_STATUS_LABELS[linkStatus]}
          </span>
        </div>
        {canCopyLink && inviteUrl && (
          <Button
            type='button'
            variant='secondary'
            size='sm'
            icon={<Copy size={14} />}
            onClick={() => onCopy(inviteUrl)}
          >
            Copiar link
          </Button>
        )}
      </div>

      <p className='mt-3 text-sm leading-relaxed text-text-muted'>
        {G5_LINK_STATUS_DESCRIPTIONS[linkStatus]}
      </p>

      {inviteUrl && linkStatus !== 'NAO_GERADO' && (
        <div className='mt-3 rounded-lg border border-white/[0.06] bg-bg-secondary/40 p-3'>
          <p className='text-[10px] font-bold uppercase tracking-wider text-text-muted mb-1'>
            Link gerado
          </p>
          <p className='break-all font-mono text-xs text-text-primary'>{inviteUrl}</p>
        </div>
      )}

      {expiresAt && (
        <p className='mt-2 text-xs text-text-muted'>
          {usedAt
            ? `Enviado em ${formatSecurityDate(usedAt)}`
            : timeLabel
              ? timeLabel
              : `Expira em ${formatSecurityDate(expiresAt)}`}
        </p>
      )}
    </div>
  )
}

export function FinanceG5ContratacaoSection({ detail, canGenerateG5Invite, onReload }: Props) {
  const [generating, setGenerating] = useState(false)
  const g5 = detail.g5PjLink
  const linkStatus: G5LinkStatus = g5?.linkStatus ?? 'NAO_GERADO'
  const inviteUrl = g5?.invite?.url ?? null
  const completeness = g5?.completeness

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      await financeService.createG5PjInvite(detail.id)
      toast.success('Link de cadastro gerado')
      onReload()
    } catch {
      toast.error('Não foi possível gerar o link de cadastro')
    } finally {
      setGenerating(false)
    }
  }

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copiado')
    } catch {
      toast.error('Não foi possível copiar o link')
    }
  }

  return (
    <FinanceDetailSectionCard title='Cadastro do colaborador PJ' icon={UserPlus} accent='#10b981'>
      <div className='space-y-4 text-sm'>
        <p className='text-text-muted leading-relaxed'>
          {canGenerateG5Invite
            ? 'Gere o link para o profissional preencher documentos, endereço e dados bancários. O financeiro revisa o cadastro neste chamado e complementa o que faltar.'
            : 'Acompanhe se o financeiro já gerou o link de cadastro e se o profissional já enviou o formulário.'}
        </p>

        <G5LinkStatusCard
          linkStatus={linkStatus}
          inviteUrl={inviteUrl}
          expiresAt={g5?.invite?.expiresAt ?? null}
          usedAt={g5?.invite?.usedAt ?? null}
          canCopyLink={Boolean(inviteUrl)}
          onCopy={(url) => void copyLink(url)}
        />

        {canGenerateG5Invite && (
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              variant='primary'
              size='sm'
              icon={generating ? <Loader2 size={14} className='animate-spin' /> : <Link2 size={14} />}
              disabled={generating}
              onClick={() => void handleGenerate()}
            >
              {inviteUrl ? 'Regenerar link de cadastro' : 'Gerar link de cadastro'}
            </Button>
            {inviteUrl && (
              <Button
                type='button'
                variant='secondary'
                size='sm'
                icon={<Copy size={14} />}
                onClick={() => void copyLink(inviteUrl)}
              >
                Copiar link
              </Button>
            )}
          </div>
        )}

        {g5?.pj && (
          <div className='space-y-3'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div>
                <p className='font-semibold text-text-primary'>{g5.pj.nomeCompleto}</p>
                <p className='text-xs text-text-muted'>
                  Status PJ:{' '}
                  {PJ_AGRADA_STATUS_LABELS[g5.pj.status as keyof typeof PJ_AGRADA_STATUS_LABELS] ??
                    g5.pj.status}
                </p>
              </div>
              {completeness && (
                <span
                  className={cn(
                    'rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider',
                    completeness.complete
                      ? 'bg-emerald-500/10 text-emerald-600'
                      : 'bg-amber-500/10 text-amber-700',
                  )}
                >
                  {completeness.percent}% preenchido
                </span>
              )}
            </div>

            {completeness && (
              <div className='rounded-lg border border-white/[0.06] p-3'>
                <p className='text-[10px] font-bold uppercase tracking-wider text-text-muted mb-2'>
                  {completeness.complete ? 'Cadastro completo' : 'Pendências para revisão'}
                </p>
                <ul className='space-y-1.5'>
                  {completeness.fields.map((field) => (
                    <li key={field.key} className='flex items-center gap-2 text-xs'>
                      {field.ok ? (
                        <CheckCircle2 size={14} className='shrink-0 text-emerald-600' />
                      ) : (
                        <Circle size={14} className='shrink-0 text-amber-600' />
                      )}
                      <span className={field.ok ? 'text-text-muted' : 'text-text-primary'}>
                        {field.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </FinanceDetailSectionCard>
  )
}
